import {
  Box,
  Paper,
  Tooltip as TooltipMUI,
  Typography,
} from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  YAxis,
} from 'recharts'

import {
  RecentDiagnosisData,
  TotalHerbicidesData,
} from '@/services/get-herbicides-economy'

import { formatNumberToBrazilianCurrency } from '../../utils/format-number-to-brazilian-currency'
import { useStyles } from './styles'

type ChartData = TotalHerbicidesData[] | RecentDiagnosisData[]

export interface CustomBarChartProps {
  data: ChartData
  chartType: 'total' | 'diagnosis'
  title: string
  tooltipMessage: string
}

export function CustomBarChart({
  data,
  chartType,
  title,
  tooltipMessage,
}: CustomBarChartProps) {
  const classes = useStyles()

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload
      if (chartType === 'total') {
        return (
          <div className={classes.tooltip}>
            <p>
              Sem Cromai: {formatNumberToBrazilianCurrency(item.without_cromai)}
            </p>
            <p>
              Com Cromai: {formatNumberToBrazilianCurrency(item.with_cromai)}
            </p>
          </div>
        )
      }
      if (chartType === 'diagnosis') {
        return (
          <div className={classes.tooltip}>
            <p>{item.demand_name}</p>
            <p>
              Sem Cromai:{' '}
              {formatNumberToBrazilianCurrency(
                item.herbicides_per_diagnosis.without_cromai,
              )}
            </p>
            <p>
              Com Cromai:{' '}
              {formatNumberToBrazilianCurrency(
                item.herbicides_per_diagnosis.with_cromai,
              )}
            </p>
          </div>
        )
      }
    }
    return null
  }
  const CustomLegend = ({ payload }: any) => {
    return (
      <ul className={classes.legend}>
        {payload.map((entry: any, index: any) => (
          <li key={`item-${index}`}>
            <div
              className={classes.cicle}
              style={{
                backgroundColor: entry.color,
              }}
            />
            <span>{entry.value}</span>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <Paper elevation={2} className={classes.barChart}>
      <Box display='flex' alignItems='center' marginBottom='4rem'>
        <Typography variant='h2'>{title}</Typography>
        <TooltipMUI
          title={tooltipMessage}
          placement='right'
          classes={{
            tooltip: classes.tooltipInfo,
          }}
        >
          <InfoOutlinedIcon />
        </TooltipMUI>
      </Box>
      <ResponsiveContainer width='100%' height={300}>
        <BarChart data={data} barSize={16} barGap={8}>
          <CartesianGrid vertical={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#c2c2c260' }} />
          <YAxis
            tick={{
              fill: '#718EBF',
              fontSize: 14,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
            }}
            axisLine={false}
            tickLine={false}
          />
          <Legend
            content={<CustomLegend />}
            payload={[
              { value: 'Sem Cromai', color: '#AAACA7' },
              { value: 'Com Cromai', color: '#93B83F' },
            ]}
          />
          {chartType === 'total' && (
            <>
              <Bar
                dataKey='without_cromai'
                fill='#AAACA7'
                radius={[10, 10, 0, 0]}
                id='without_cromai'
              />
              <Bar
                dataKey='with_cromai'
                fill='#93B83F'
                radius={[10, 10, 0, 0]}
              />
            </>
          )}
          {chartType === 'diagnosis' && (
            <>
              <Bar
                dataKey='herbicides_per_diagnosis.without_cromai'
                fill='#AAACA7'
                radius={[10, 10, 0, 0]}
              />
              <Bar
                dataKey='herbicides_per_diagnosis.with_cromai'
                fill='#93B83F'
                radius={[10, 10, 0, 0]}
              />
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  )
}
